import React from 'react';
import { graphql, ChildProps } from 'react-apollo';
import gql from 'graphql-tag';
import { get } from 'lodash';
import { stringify } from 'qs';
import bind from '../../bind';
import { CommonsCmsCategoryCollection } from '../../rql';
import OverflowingRow from '../../components/overflowing_row';
import Tiles from '../../components/tiles';
import CoreLink from '../../components/core_link';
import SanitizedRender from '../../components/sanitized_render';

import { IDynamicComponentProps } from '../dynamic_component_props';

export interface IProps extends IDynamicComponentProps {
  categoryUUIDs?: string[];
  linkOverride?: string;
  title?: string;
  titleHtml?: string;
  subtitleHtml?: string;
  ledeHtml?: string;
  jumplinkSlug?: string;
}

export class CategoryCollection extends React.Component<ChildProps<IProps, CommonsCmsCategoryCollection.Query>, null> {
  queryString(cat) {
    if (cat.rootSlug) {
      return stringify({ product_type: cat.rootSlug, category: cat.slug });
    }

    return stringify({ product_type: cat.slug });
  }

  getLink(cat) {
    if (this.props.linkOverride) {
      if (this.queryString(cat)) {
        return `${this.props.linkOverride}?${this.queryString(cat)}`;
      }
      return this.props.linkOverride;
    }

    return cat.cmsLink.href;
  }

  isEmpty() {
    return !this.props.data.loading && !get(this.props.data.categories, 'length');
  }

  isLoading() {
    return this.props.data.loading;
  }

  getBackgroundImageStyle(cat) {
    const src = get(cat.images[0], 'source');
    if (!src) return null;

    return { backgroundImage: `url(${src})` };
  }

  @bind
  renderTile(cat, idx) {
    return (
      <CoreLink
        to={this.getLink(cat)}
        className="category-collection__item"
        style={this.getBackgroundImageStyle(cat)}
        key={idx}
      >
        <div className="category-collection__item__inner">
          <div className="category-collection__item__text">
            {cat.name}
          </div>
        </div>
      </CoreLink>
    );
  }

  renderPlaceholder() {
    return (
      <div className="category-collection__item category-collection__item--placeholder" />
    );
  }

  renderTitle() {
    return (
      <SanitizedRender
        html={this.props.titleHtml || this.props.title}
      />
    );
  }

  render() {
    if (this.isEmpty()) return null;

    const { categories } = this.props.data;
    return (
      <OverflowingRow
        collectionCount={categories && categories.length}
        title={this.renderTitle()}
        subtitleHtml={this.props.subtitleHtml}
        ledeHtml={this.props.ledeHtml}
        id={this.props.jumplinkSlug}
      >
        <Tiles
          largeTiles
          loading={this.isLoading()}
          placeholder={this.renderPlaceholder()}
          expectedCount={get(this.props.categoryUUIDs, 'length')}
          singleRow
        >
          {categories && categories.map(this.renderTile)}
        </Tiles>
      </OverflowingRow>
    );
  }
}

const connect = graphql<IProps, CommonsCmsCategoryCollection.Query, CommonsCmsCategoryCollection.Variables>(
  gql`
  query Commons_Cms_CategoryCollection(
    $categoryUuids: [String]
  ) {
    categories(input:{
      ids: $categoryUuids
    }) {
      _id
      id
      name
      slug
      rootSlug
      cmsLink {
        href
      }
      images(input:{
        type:"Category",
        count:1,
        transform:"card_wide",
        scope:"full_bleed_brand"
      }) {
        source
      }
    }
  }
  `,
  {
    options: ownProps => ({
      ssr: ownProps.ssr,
      variables: {
        categoryUuids: ownProps.categoryUUIDs,
      },
    }),
  },
);

export default connect(CategoryCollection);
